import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  base64URLStringToBuffer,
  WebAuthnAbortService,
} from "@simplewebauthn/browser";
import { Endpoint } from "../shared/endpoints";
import { environment } from "src/environments/environment";

export type PasskeyAttachmentMode = "any" | "platform" | "cross-platform";

export interface PasskeyCredentialItem {
  credentialId: string;
  friendlyName: string;
  createdUtc: string;
  lastUsedUtc?: string;
  transports?: string;
  authenticatorAttachment?: string;
}

/** Hybrid QR ceremonies need several minutes. */
const CEREMONY_TIMEOUT_MS = 300_000;

@Injectable({
  providedIn: "root",
})
export class PasskeyService {
  constructor(private http: HttpClient) {}

  isSupported(): boolean {
    try {
      return (
        typeof window !== "undefined" &&
        !!window.PublicKeyCredential &&
        browserSupportsWebAuthn()
      );
    } catch {
      return false;
    }
  }

  /** Cancels an in-flight Conditional UI / WebAuthn ceremony. */
  abortPendingCeremony(): void {
    try {
      WebAuthnAbortService.cancelCeremony();
    } catch {
      // ignore
    }
  }

  mapError(error: any): string {
    if (!error) {
      return "Something went wrong. Please try again or use your password.";
    }

    const name = error.name || error.error?.name;
    const message = (error.message || error.error?.message || "").toString();

    if (name === "NotAllowedError" || name === "AbortError") {
      return "Passkey sign-in was cancelled or biometrics failed. You can try again or use your password.";
    }
    if (
      /no app found|no application found|can't open|cannot open|unsupported url|unsupported link/i.test(
        message
      )
    ) {
      return (
        "Your phone could not open the Passkey QR (FIDO code). Do not use a generic QR scanner. " +
        "Scan with the device that already has your Passkey (Camera / Google Password Manager / iPhone Passkeys). " +
        "Finish stays on the computer — the phone does not open Flowchest. " +
        "Easier option: Create passkey on this device with Windows Hello."
      );
    }
    if (
      /not found|no passkey|no matching|credential.*not|couldn't find|could not find/i.test(
        message
      )
    ) {
      return (
        "No Passkey for this site was found on that phone. " +
        "Create a Passkey on this computer first, or create one on the phone from Settings → Passkeys, then try Sign in with Passkey again."
      );
    }
    if (name === "InvalidStateError") {
      return "This Passkey is already registered on your account.";
    }
    if (name === "NotSupportedError") {
      return "Passkeys are not supported on this browser or device.";
    }
    if (name === "SecurityError") {
      const host =
        typeof window !== "undefined" ? window.location.hostname : "";
      const isLocal =
        host === "localhost" || host === "127.0.0.1" || host.endsWith(".local");
      const isAllowedHost =
        host === "flowchest.com" ||
        host.endsWith(".flowchest.com") ||
        host.endsWith(".azurestaticapps.net");

      if (isLocal) {
        return (
          "Passkeys for production only work on https://app.flowchest.com " +
          "or your Azure Static Web Apps HTTPS URL. " +
          "On localhost, use the Development API (RP id localhost)."
        );
      }
      if (host && !isAllowedHost) {
        return (
          `Passkeys are blocked on "${host}". Use https://app.flowchest.com ` +
          "or an Azure Static Web Apps HTTPS URL."
        );
      }
      return (
        "Passkey was blocked by the browser. Use HTTPS on an allowed site " +
        "(app.flowchest.com or Azure Static Web Apps), then try again."
      );
    }
    if (error.status === 401) {
      return (
        error.error?.message ||
        message ||
        "Passkey authentication failed. Please try again or use your password."
      );
    }
    if (error.status === 400 && (error.error?.message || message)) {
      return error.error?.message || message;
    }
    if (message) {
      return message;
    }
    return "Passkey operation failed. Please try again or use your password.";
  }

  list(): Promise<PasskeyCredentialItem[]> {
    return firstValueFrom(
      this.http.get<PasskeyCredentialItem[]>(
        environment.apiBaseUrl + Endpoint.passkeyList
      )
    );
  }

  /** Yahoo-style: after email, check whether Passkey sign-in should be offered. */
  async hasPasskeys(email: string): Promise<boolean> {
    if (!email?.trim()) {
      return false;
    }
    try {
      const result = await firstValueFrom(
        this.http.post<{ hasPasskeys: boolean }>(
          environment.apiBaseUrl + Endpoint.passkeyLoginCheck,
          { email: email.trim().toLowerCase() }
        )
      );
      return !!result?.hasPasskeys;
    } catch {
      return false;
    }
  }

  async register(
    friendlyName?: string,
    authenticatorAttachment: PasskeyAttachmentMode = "any"
  ): Promise<PasskeyCredentialItem> {
    this.abortPendingCeremony();

    const optionsResponse = await firstValueFrom(
      this.http.post<any>(
        environment.apiBaseUrl + Endpoint.passkeyRegisterOptions,
        { authenticatorAttachment }
      )
    );

    const attestationResponse = await startRegistration(
      this.normalizeRegistrationOptions(
        optionsResponse.options,
        authenticatorAttachment
      )
    );

    const transports =
      attestationResponse?.response?.transports || undefined;

    const verifyResult = await firstValueFrom(
      this.http.post<any>(
        environment.apiBaseUrl + Endpoint.passkeyRegisterVerify,
        {
          sessionId: optionsResponse.sessionId,
          friendlyName: friendlyName || undefined,
          attestationResponse,
          transports,
          authenticatorAttachment,
        }
      )
    );

    return verifyResult.credential;
  }

  async authenticate(email?: string, useBrowserAutofill = false): Promise<any> {
    if (!useBrowserAutofill) {
      this.abortPendingCeremony();
    }

    const optionsResponse = await firstValueFrom(
      this.http.post<any>(
        environment.apiBaseUrl + Endpoint.passkeyLoginOptions,
        { email: email || null }
      )
    );

    const assertionResponse = await startAuthentication(
      this.normalizeAuthenticationOptions(optionsResponse.options),
      useBrowserAutofill
    );

    return firstValueFrom(
      this.http.post<any>(
        environment.apiBaseUrl + Endpoint.passkeyLoginVerify,
        {
          sessionId: optionsResponse.sessionId,
          assertionResponse,
        }
      )
    );
  }

  async authenticateWithAutofill(email?: string): Promise<any> {
    if (!(await browserSupportsWebAuthnAutofill())) {
      return null;
    }
    return this.authenticate(email, true);
  }

  revoke(credentialId: string): Promise<any> {
    const encoded = encodeURIComponent(credentialId);
    return firstValueFrom(
      this.http.delete<any>(
        environment.apiBaseUrl + Endpoint.passkeyRevoke + encoded
      )
    );
  }

  rename(credentialId: string, friendlyName: string): Promise<any> {
    const encoded = encodeURIComponent(credentialId);
    return firstValueFrom(
      this.http.put<any>(
        environment.apiBaseUrl + Endpoint.passkeyRename + encoded,
        { friendlyName }
      )
    );
  }

  /**
   * Fido2NetLib sends user.id as base64url(bytes). @simplewebauthn/browser v9
   * treats user.id as a UTF-8 string and re-encodes it — that corrupts the
   * userHandle. Decode to the original UTF-8 user id (email) before create.
   */
  private normalizeRegistrationOptions(
    options: any,
    authenticatorAttachment: PasskeyAttachmentMode = "any"
  ): any {
    if (!options) {
      return options;
    }

    const normalized = { ...options };
    delete normalized.status;
    delete normalized.errorMessage;

    if (normalized.user?.id && typeof normalized.user.id === "string") {
      normalized.user = {
        ...normalized.user,
        id: this.fidoBase64UrlUserIdToUtf8(normalized.user.id),
      };
    }

    if (!normalized.timeout || normalized.timeout < CEREMONY_TIMEOUT_MS) {
      normalized.timeout = CEREMONY_TIMEOUT_MS;
    }

    const selection: any = {
      ...(normalized.authenticatorSelection || {}),
      requireResidentKey: true,
      residentKey: "required",
      userVerification: "preferred",
    };

    if (authenticatorAttachment === "any") {
      delete selection.authenticatorAttachment;
      normalized.hints = ["client-device", "hybrid"];
    } else if (authenticatorAttachment === "cross-platform") {
      selection.authenticatorAttachment = "cross-platform";
      normalized.hints = ["hybrid", "security-key"];
    } else {
      selection.authenticatorAttachment = "platform";
      normalized.hints = ["client-device"];
    }

    normalized.authenticatorSelection = selection;
    return normalized;
  }

  private normalizeAuthenticationOptions(options: any): any {
    if (!options) {
      return options;
    }

    const normalized = { ...options };
    delete normalized.status;
    delete normalized.errorMessage;

    if (!normalized.timeout || normalized.timeout < CEREMONY_TIMEOUT_MS) {
      normalized.timeout = CEREMONY_TIMEOUT_MS;
    }

    // Yahoo-style: this device first, then phone hybrid QR for cross-device.
    normalized.hints = ["client-device", "hybrid"];
    normalized.allowCredentials = [];

    return normalized;
  }

  private fidoBase64UrlUserIdToUtf8(value: string): string {
    try {
      const bytes = new Uint8Array(base64URLStringToBuffer(value));
      const asText = new TextDecoder("utf-8").decode(bytes);
      if (asText && !/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/.test(asText)) {
        return asText;
      }
    } catch {
      // Already a plain string (or not base64url) — pass through.
    }
    return value;
  }
}
